/* eslint-disable @typescript-eslint/no-explicit-any */
import {AfterViewInit, ChangeDetectorRef, Component, DestroyRef, DoCheck, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CommonModule, NgClass} from '@angular/common';
import {NlrFormFieldHasErrorPipe} from '../../../common/pipe/nlr-form-field-has-error.pipe';
import {FormsModule, NgForm} from '@angular/forms';
import {NlrFocusDirective} from '../../../common/directive/nlr-focus.directive';
import {MaxLengthInputLimitDirective} from '../../../common/directive/max-length-input-limit.directive';
import {ConstructionTool} from '../../../common/models/construction-tool.model';
import {WidgetService} from '../../services/widget.service';
import {MapToolsService} from '../../services/map-tools.service';
import {WidgetConfigLookup} from '../../../common/models/widget-config.model';
import {LookupService} from '../../../common/service/lookup.service';
import {UserPreferencesService} from '../../../common/service/user-preferences.service';
import {TraitsService} from '../../../common/service/traits.service';
import {ConstructionToolModelService} from './construction-tool/services/construction-tool-model.service';
import {NlrHasPermissionsPipe} from '../../../common/pipe/nlr-has-permissions.pipe';
import {SpatialUtilService} from '../../services/spatial-util.service';
import {MapModeService} from '../../services/map-mode.service';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {MapService} from '../../services/map.service';
import {ConstructionToolComponent} from './construction-tool/construction-tool.component';
import {NlrPermissionsDisabledPipe} from '../../../common/pipe/nlr-permissions-disabled-pipe';
import {ConstructionToolServiceFactory} from './construction-tool/construction-tool-service.factory';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CdkDrag, CdkDragHandle, DragRef, Point} from '@angular/cdk/drag-drop';
import {PermissionsService} from '../../../common/service/permissions.service';
import {WidgetWindow, WindowWidgetsService} from '../../window-widgets/window-widgets.service';
import {MergeLinesConstructionToolService} from './construction-tool/services/merge-lines-construction-tool.service';
import {MergePointsConstructionToolService} from './construction-tool/services/merge-points-construction-tool.service';
import {LineService} from '../../services/features/line.service';
import {AngleUtilService} from '../../services/features/angle/angle-util.service';
import {AlertModalService} from '../../../common/component/modal';
import {AnnotationService} from '../../services/features/annotation.service';
import {FeatureApplyDeleteService} from '../../services/feature-apply-delete.service';
import {PolygonUtilService} from '../../services/features/polygon-util.service';
import {SpatialSnapshotService} from '../../snapshot/spatial-snapshot.service';
import {SpatialSequenceService} from '../../../common/service/spatial-sequence.service';
import {LotConstructionToolService} from './construction-tool/services/lot-construction-tool.service';
import {MapEventsService} from '../../services/map-events.service';
import {GraphicSelectionService} from '../../services/graphic-selection.service';
import {PlanModelService} from '../../../common/service/plan-model.service';
import {HELP_LINK_CONTENTS_BASE_URL} from '../../../common/constants';
import {HelpLinkComponent} from '../../../help/help-link.component';
import {AppConfigService} from '../../../common/service/app-config.service';
import {AuthenticatorService} from '../../../common/service/authenticator.service';
import {EsriModuleService} from '../../esri-nlr/services/esri-module.service';
import {ArcGisAuthService} from '../../services/arc-gis-auth.service';
import {debounceTime, interval, of, take, tap, timer} from 'rxjs';

@Component({
    selector: 'construction-tools',
    imports: [
        NgClass,
        CommonModule,
        NlrFormFieldHasErrorPipe,
        FormsModule,
        NlrFocusDirective,
        MaxLengthInputLimitDirective,
        NlrHasPermissionsPipe,
        ConstructionToolComponent,
        NlrPermissionsDisabledPipe,
        CdkDrag,
        CdkDragHandle,
        HelpLinkComponent
    ],
    templateUrl: './construction-tools.component.html',
    styleUrl: './construction-tools.component.scss'
})
export class ConstructionToolsComponent implements OnInit, AfterViewInit, DoCheck, OnDestroy {
    @ViewChild('constructionToolsForm')
    constructionToolsForm: NgForm;

    id = crypto.randomUUID();

    title = 'ConstructionToolsComponent';

    widgetId = 'constructionTools';

    widgetConfig: WidgetConfigLookup = {};

    open = true;

    toolRows: {tools: ConstructionTool[]}[] = [{tools: []}];

    windowMode = !!(window as WidgetWindow).nlrpContext;

    position = {x: -1, y: -1};

    widgetWidth: number;

    widgetHeight: number;

    visibleAtLastCheck = false;

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private destroyRef: DestroyRef,
        private eventBus: NgEventBus,
        private appConfigService: AppConfigService,
        private authenticatorService: AuthenticatorService,
        private arcGisAuthService: ArcGisAuthService,
        private alertModalService: AlertModalService,
        private angleUtilService: AngleUtilService,
        private annotationService: AnnotationService,
        private constructionToolServiceFactory: ConstructionToolServiceFactory,
        private constructionToolModelService: ConstructionToolModelService,
        private esriModuleService: EsriModuleService,
        private featureApplyDeleteService: FeatureApplyDeleteService,
        private graphicSelectionService: GraphicSelectionService,
        private lineService: LineService,
        private lookupService: LookupService,
        private lotConstructionToolService: LotConstructionToolService,
        private mapEventsService: MapEventsService,
        private mapModeService: MapModeService,
        private mapService: MapService,
        private mapToolsService: MapToolsService,
        private mergeLinesConstructionToolService: MergeLinesConstructionToolService,
        private mergePointsConstructionToolService: MergePointsConstructionToolService,
        private permissionsService: PermissionsService,
        private planModelService: PlanModelService,
        private polygonUtilService: PolygonUtilService,
        private spatialSequenceService: SpatialSequenceService,
        private spatialSnapshotService: SpatialSnapshotService,
        private spatialUtilService: SpatialUtilService,
        private traitsService: TraitsService,
        private userPreferencesService: UserPreferencesService,
        private widgetService: WidgetService,
        private windowWidgetsService: WindowWidgetsService
    ) {}

    ngOnInit() {
        console.debug(`${this.title} - ngOnInit`);

        this.widgetConfig = this.lookupService.getLookups('widgetConfig').find((l: WidgetConfigLookup) => l.name === this.widgetId);

        this.eventBus
            .on('deactivateConstructionTools')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.title} - on deactivateConstructionTools`);

                if (this.getSelectedTool() && this.isCreateActive()) {
                    this.toggleCreate(false);

                    const selectedTool = this.getSelectedTool();
                    selectedTool.errors.length = 0;
                    selectedTool.warnings.length = 0;
                    selectedTool.information.length = 0;
                    this.eventBus.cast(`constructionToolService:${selectedTool.type}:clear`);
                    this.resetActionBehaviours();
                }

                this.mapToolsService.deactivate('CONSTRUCTION_TOOLS');
            });

        this.eventBus
            .on('toggleConstructionToolsCreateButton')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                console.debug(`${this.title} - on toggleConstructionToolsCreateButton`);
                this.toggleCreate(meta.data);
            });

        this.eventBus
            .on('resetActionBehaviours')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.title} - on resetActionBehaviours`);
                this.resetActionBehaviours();
            });

        this.eventBus
            .on('resizeConstructionToolBody')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                console.debug(`${this.title} - on resizeConstructionToolBody`);
                this.resizeConstructionToolBody(meta.data);
            });

        const windowContext = (window as WidgetWindow).nlrpContext;

        if (!windowContext) {
            const dockOutConstToolsWidget = this.userPreferencesService.userPreferences.undockedWindows.includes('CONSTRUCTION_TOOLS_WINDOW');

            if (dockOutConstToolsWidget) {
                this.openWidgetWindow();
            }
        }

        if (windowContext) {
            windowContext.childChangeDetectorRef = this.changeDetectorRef;
            window.addEventListener('resize', () => this.resizeConstructionToolBody());

            // Interval ensures child window responds to changes in main window e.g. switching to an enlargement should disable construction tools
            interval(100).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        }

        this.populateTools();

        if (!windowContext) {
            this.widgetService.setupWidget(this.widgetId, () => this.resizeConstructionToolBody());
        }
    }

    ngAfterViewInit() {
        this.widgetService.setupWidget(this.widgetId);
    }

    ngDoCheck() {
        const visible = this.isVisible();
        if (visible && !this.visibleAtLastCheck && !(window as WidgetWindow).nlrpContext) {
            window.setTimeout(() => this.widgetService.setupWidget(this.widgetId, () => this.resizeConstructionToolBody()), 0);
        }
        this.visibleAtLastCheck = visible;
    }

    ngOnDestroy(): void {
        const selectedTool = this.getSelectedTool();
        console.log(`{this.title} - ngOnDestroy ${selectedTool.type}`);
        this.windowWidgetsService.closeAll();
    }

    trackByToolId(index: number, tool: ConstructionTool) {
        return tool.id;
    }

    computeDragPosition = (userPointerPosition: Point, dragRef: DragRef, dimensions: DOMRect): Point => {
        return this.widgetService.computeDragPosition(userPointerPosition, dragRef, dimensions);
    };

    private setFeatureLayersMouseEventsEnabled(enabled: boolean) {
        ['affectedlot', 'constructline', 'constructpoint', 'controlpoint', 'line', 'lot', 'polygon', 'road'].forEach(featureClass => {
            const featureLayer = this.spatialUtilService.getMapFeatureLayer(featureClass);
            if (enabled) {
                featureLayer.enableMouseEvents();
            } else {
                featureLayer.disableMouseEvents();
            }
        });
    }

    getSelectedTool() {
        return this.constructionToolModelService.getSelectedTool();
    }

    getSelectedToolType() {
        return this.constructionToolModelService.getSelectedToolType();
    }

    getSelectedToolId() {
        return this.getSelectedTool().id;
    }

    getSelectedToolPermissions() {
        return this.getSelectedTool().permissions;
    }

    getCreateButtonId() {
        return `${this.getSelectedToolType()}ConstructionTool${this.isCreateActive() ? 'Finish' : 'Start'}Btn`;
    }

    getCreateButtonLabel() {
        const selectedTool = this.getSelectedTool();
        return this.isCreateActive() ? selectedTool.finishButtonLabel : selectedTool.createButtonLabel;
    }

    getFinishButtonLabel() {
        return this.getSelectedTool().finishButtonLabel;
    }

    getFeatureButtonLabel() {
        return this.getSelectedTool().featureButtonLabel;
    }

    getClearButtonLabel() {
        return this.getSelectedTool().clearButtonLabel;
    }

    getCloseButtonLabel() {
        return this.getSelectedTool().closeButtonLabel;
    }

    isCreateActive() {
        return this.constructionToolModelService.createActive;
    }

    getHelpUrl() {
        const toolTypeKebabCase = this.getSelectedToolType()
            .split(/(?=[A-Z])/)
            .map(v => v.toLowerCase())
            .join('-');
        return HELP_LINK_CONTENTS_BASE_URL + `construction-tools-${toolTypeKebabCase}-help.html`;
    }

    getHelpPageTitle() {
        const toolDescription = {
            angle: 'Create an Angle',
            boundaryOffset: 'Create a Boundary Offset',
            controlPoint: 'Create a Control Point',
            cornerOffset: 'Create a Corner Offset',
            dissectLine: 'Dissect a Line',
            line: 'Construct a Line',
            lot: 'Create a Lot',
            mergeLines: 'Merge Lines',
            mergePoints: 'Merge Points',
            point: 'Create a Point',
            polygon: 'Construct a Polygon',
            polyline: 'Construct a Polyline',
            totalBoundaryDistance: 'Create a Total Boundary Distance'
        }[this.getSelectedToolType()];

        return `Construction Tool | ${toolDescription}`;
    }

    isVisible() {
        return (
            !this.mapToolsService.isInactive('CONSTRUCTION_TOOLS_WINDOW') &&
            ((window as WidgetWindow).nlrpContext || !this.windowWidgetsService.isOpen('CONSTRUCTION_TOOLS_WINDOW'))
        );
    }

    isToolDisabled(tool: ConstructionTool) {
        return tool.disabled || !this.mapModeService.isMapModeType('mainPlan');
    }

    clearIsDisabled() {
        return this.isToolDisabled(this.getSelectedTool());
    }

    createIsDisabled() {
        const selectedTool = this.getSelectedTool();
        return (
            !this.mapModeService.isMapModeType('mainPlan') ||
            this.mapToolsService.isSuspended('CONSTRUCTION_TOOLS_WINDOW') ||
            (selectedTool.createIsDisabled && selectedTool.createIsDisabled())
        );
    }

    applyFeatureAction() {
        console.debug(`${this.title} - applyFeatureAction`);
        this.featureBlocked = true;
        this.getSelectedTool().applyFeatureAction();
        timer(300)
            .pipe(take(1))
            .subscribe(() => {
                this.featureBlocked = false;
            });
    }

    applyFeatureActionIsDisabled() {
        const selectedTool = this.getSelectedTool();
        return (
            !this.mapModeService.isMapModeType('mainPlan') ||
            (selectedTool.applyFeatureActionIsDisabled && selectedTool.applyFeatureActionIsDisabled())
        );
    }

    clear(unhighlightFeatures: boolean) {
        this.clearBlocked = true;
        console.debug(`${this.title} - clear`);
        const selectedTool = this.getSelectedTool();
        selectedTool.errors.length = 0;
        selectedTool.warnings.length = 0;
        selectedTool.information.length = 0;
        this.eventBus.cast(`constructionToolService:${selectedTool.type}:clear`, unhighlightFeatures);
        this.eventBus.cast(`constructionTool:${selectedTool.type}:clear`);
        this.resetActionBehaviours();
        timer(300)
            .pipe(take(1))
            .subscribe(() => {
                this.clearBlocked = false;
            });
    }

    changeSelectedConstructionTool(tool: ConstructionTool) {
        console.debug(`${this.title} - changeSelectedConstructionTool`);
        this.constructionToolModelService.setSelectedTool(tool);
        window.setTimeout(() => this.resizeConstructionToolBody());
    }

    createBlocked = false;
    featureBlocked = false;
    clearBlocked = false;

    isButtonBlocked() {
        return this.createBlocked || this.featureBlocked || this.clearBlocked;
    }

    create() {
        this.createBlocked = true;
        console.debug(`${this.title} - create`);

        const windowContext = (window as WidgetWindow).nlrpContext;

        const selectedToolType = this.getSelectedToolType();

        if (this.getSelectedTool().stickyButton) {
            if (this.isCreateActive()) {
                this.toggleCreate(false);
                const constructionToolService = this.constructionToolServiceFactory.getConstructionToolService(selectedToolType);
                if (constructionToolService.finish) {
                    constructionToolService.finish().subscribe(() => this.mapToolsService.deactivate('CONSTRUCTION_TOOLS'));
                } else {
                    this.mapToolsService.deactivate('CONSTRUCTION_TOOLS');
                }
            } else {
                this.toggleCreate(true);
                this.eventBus.cast(`constructionTool:${this.getSelectedToolType()}:create`, this.id);
            }

            if (windowContext) {
                windowContext.changeDetectorRef.detectChanges();
            }
        } else {
            if (windowContext) {
                windowContext.changeDetectorRef.detectChanges();
            }
            this.eventBus.cast(`constructionTool:${this.getSelectedToolType()}:create`, this.id);
        }
        //stop from double-clicking
        timer(300)
            .pipe(take(1))
            .subscribe(() => {
                this.createBlocked = false;
            });
    }

    showOrHideWidgetContent() {
        this.widgetService.showOrHideWidgetContent(this.widgetId, this.open);
        this.open = !this.open;
    }

    private resetActionBehaviours() {
        console.debug(`${this.title} - resetActionBehaviours`);

        this.toggleCreate(false);

        this.mapService.disableMeasurementTool();
        this.mapService.disableSnappingTool();
        this.setFeatureLayersMouseEventsEnabled(false);

        window.setTimeout(() => {
            if (this.widgetService.isWidgetActive('IdentifyFeatures')) {
                this.eventBus.cast('resetFeaturesInteracted');
            }
        }, 250);
    }

    toggleCreate(active: boolean) {
        console.debug(`${this.title} - toggleCreate`);
        this.constructionToolModelService.createActive = active;

        if (!(window as WidgetWindow).nlrpContext) {
            if (this.windowWidgetsService.isOpen('CONSTRUCTION_TOOLS_WINDOW')) {
                this.windowWidgetsService.refreshAll();
            }
        }
    }

    populateTools() {
        console.debug(`${this.title} - populateTools`);

        const constructionTools: ConstructionTool[] = this.lookupService.getLookups('constructionsToolsItem');

        const filteredConstructionToolsItemLookups = constructionTools.filter(l => this.traitsService.criteriaMatches(l.planTraitCriteria, true));

        filteredConstructionToolsItemLookups.forEach(tool => {
            tool.isVisible = tool.active;
            tool.errors = [];
            tool.showErrors = true;
            tool.warnings = [];
            tool.showWarnings = true;
            tool.information = [];
            tool.showInformation = true;

            if (this.toolRows.length >= tool.row) {
                this.toolRows[tool.row - 1].tools.push(tool);
                this.toolRows[tool.row - 1].tools.sort((a, b) => a.order - b.order);
            } else {
                this.toolRows.push({tools: [tool]});
            }

            if (!this.windowMode && tool.defaultSelection) {
                this.constructionToolModelService.setSelectedTool(tool);
            }
        });
    }

    openWidgetWindow() {
        console.debug(`${this.title} - openWidgetWindow`);

        this.updateWidgetPosition();

        const minWidth = this.widgetConfig.minWidth ? parseInt(this.widgetConfig.minWidth) : 0;
        const minHeight = this.widgetConfig.minHeight ? parseInt(this.widgetConfig.minHeight) : 0;
        const widgetWindow = this.windowWidgetsService.open({
            name: 'CONSTRUCTION_TOOLS_WINDOW',
            title: 'Construction Tools',
            minWidth: minWidth,
            minHeight: minHeight,
            widgetWidth: this.widgetWidth && minWidth ? Math.max(minWidth, this.widgetWidth) : 0,
            widgetHeight: this.widgetHeight && minHeight ? Math.max(minHeight, this.widgetHeight) : 0,
            left: this.position.x,
            top: this.position.y,
            allLookups: this.lookupService.getAllLookups(),
            userPreferences: this.userPreferencesService.userPreferences,
            closeFn: this.closeWidget.bind(this),
            changeDetectorRef: this.changeDetectorRef,
            eventBus: this.eventBus,
            appConfigService: this.appConfigService,
            authenticatorService: this.authenticatorService,
            arcGisAuthService: this.arcGisAuthService,
            angleUtilService: this.angleUtilService,
            alertModalService: this.alertModalService,
            annotationService: this.annotationService,
            esriModuleService: this.esriModuleService,
            featureApplyDeleteService: this.featureApplyDeleteService,
            constructionToolModelService: this.constructionToolModelService,
            constructionToolServiceFactory: this.constructionToolServiceFactory,
            graphicSelectionService: this.graphicSelectionService,
            lineService: this.lineService,
            lookupService: this.lookupService,
            lotConstructionToolService: this.lotConstructionToolService,
            mapEventsService: this.mapEventsService,
            mapModeService: this.mapModeService,
            mapService: this.mapService,
            mapToolsService: this.mapToolsService,
            mergeLinesConstructionToolService: this.mergeLinesConstructionToolService,
            mergePointsConstructionToolService: this.mergePointsConstructionToolService,
            permissionsService: this.permissionsService,
            planModelService: this.planModelService,
            polygonUtilService: this.polygonUtilService,
            spatialSequenceService: this.spatialSequenceService,
            spatialSnapshotService: this.spatialSnapshotService,
            spatialUtilService: this.spatialUtilService,
            traitsService: this.traitsService,
            userPreferencesService: this.userPreferencesService,
            widgetService: this.widgetService,
            windowWidgetsService: this.windowWidgetsService
        });

        const interval = setInterval(() => {
            if (widgetWindow.closed) {
                clearInterval(interval);
                this.mapToolsService.deactivateIncompatibleTools('CONSTRUCTION_TOOLS_WINDOW');
            }
        }, 500);
    }

    closeWidgetWindow(dock: boolean) {
        console.info(`${this.title} - closeWidgetWindow - dock: ${dock}`);
        const windowContext = (window as WidgetWindow).nlrpContext;

        if (windowContext) {
            if (dock) {
                windowContext.docking = true;
                window.close();
            }
        } else {
            window.focus();
            if (!this.open) {
                this.open = true;
            }
            if (dock) {
                window.setTimeout(() => {
                    this.mapToolsService.deactivateIncompatibleTools('CONSTRUCTION_TOOLS_WINDOW');
                    this.widgetService.setupWidget(this.widgetId, () => this.resizeConstructionToolBody());
                }, 100);
            }
        }
    }

    closeWidget() {
        console.info(`${this.title} - closeWidget`);

        if (this.windowMode) {
            this.closeWidgetWindow(false);
            window.close();
            this.mapToolsService.deactivate('CONSTRUCTION_TOOLS_WINDOW');
        } else {
            this.eventBus.cast(`constructionTool:${this.getSelectedToolType()}:close`);
            this.resetActionBehaviours();
            this.mapToolsService.deactivate('CONSTRUCTION_TOOLS_WINDOW');
            this.setFeatureLayersMouseEventsEnabled(true);
        }

        this.constructionToolModelService.clearAllToolsAttrs();
        this.constructionToolModelService.clearAllSubmitted();
    }

    private updateWidgetPosition() {
        const widgetElt = document.getElementById(this.widgetId);
        if (widgetElt) {
            this.position.x = widgetElt.offsetLeft;
            this.position.y = widgetElt.offsetTop;
        }
    }

    private resizeConstructionToolBody(windowWidget?: any) {
        console.debug(`${this.title} - resizeConstructionToolBody`);

        let widget;

        if (windowWidget && windowWidget.target.document) {
            widget = windowWidget.target.document;

            const widgetBody = widget.getElementsByClassName('construction-tool-body');

            if (widget && widgetBody) {
                const widgetBodyHeight = windowWidget.target.innerHeight / 1.5;
                widgetBody[0].style.height = widgetBodyHeight + 'px';
            }

            return;
        } else {
            widget = document.getElementById(this.widgetId);
        }

        if (widget) {
            const widgetBody: HTMLElement = document.querySelector(`#${this.getSelectedToolType()}ConstructionToolTab .construction-tool-body`);
            const widgetFooter: HTMLElement = document.querySelector(`#${this.widgetId} div.inline-bottom-buttons`);
            if (widgetBody && widgetFooter) {
                let widgetBodyHeight: number;

                const widgetBodyRect = widgetBody.getBoundingClientRect();
                const widgetBodyTop = widgetBodyRect.top;
                if (this.windowMode) {
                    widgetBodyHeight = window.innerHeight - widgetBodyTop - 50;
                } else {
                    const widgetFooterRect = widgetFooter.getBoundingClientRect();
                    widgetBodyHeight = widgetFooterRect.top - widgetBodyTop;
                    if (widgetBodyHeight === 0) {
                        widgetBodyHeight = widget.clientHeight / 1.5;
                    }
                }

                widgetBody.style.height = `${widgetBodyHeight}px`;
            }

            if (widget.clientHeight && widget.clientWidth) {
                this.widgetHeight = widget.clientHeight;
                this.widgetWidth = widget.clientWidth;
            }
        }
    }
}
